import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Alert
} from '@mui/material';

const Terms = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> By using this platform, you agree to these terms and our strict no-AI policy for content creation.
          </Typography>
        </Alert>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing and using the Short Story Platform, you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: 'error.main' }}>
              2. No AI Tools Policy
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>STRICTLY PROHIBITED:</strong> The use of artificial intelligence tools, including but not limited to 
              ChatGPT, Claude, Bard, GPT-4, or any other AI writing assistants, is absolutely forbidden for creating 
              content on this platform.
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>All stories must be 100% human-authored</li>
              <li>AI-generated content will result in immediate account suspension</li>
              <li>We actively monitor for AI-generated content</li>
              <li>This policy applies to stories, comments, and all user-generated content</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              3. User Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              To access certain features of the service, you must register for an account:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>You must be at least 13 years old to create an account</li>
              <li>One account per person is allowed</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              4. Content Guidelines
            </Typography>
            <Typography variant="body1" paragraph>
              All content submitted to the platform must comply with these guidelines:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>Must be original work created by you</li>
              <li>No plagiarism or copyright infringement</li>
              <li>No hate speech, harassment, or discriminatory content</li>
              <li>No excessively violent or explicit content</li>
              <li>No spam or promotional content</li>
              <li>Must be appropriate for a general audience</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              5. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              Content ownership and rights:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>You retain ownership of your original stories and content</li>
              <li>You grant us a license to display and distribute your content on the platform</li>
              <li>You may delete your content at any time</li>
              <li>We respect intellectual property rights and respond to valid DMCA notices</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              6. Community Standards
            </Typography>
            <Typography variant="body1" paragraph>
              We expect all users to maintain respectful community interactions:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>Be respectful in comments and interactions</li>
              <li>Provide constructive feedback on stories</li>
              <li>Report inappropriate content or behavior</li>
              <li>Follow the golden rule: treat others as you'd like to be treated</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              7. Prohibited Activities
            </Typography>
            <Typography variant="body1" paragraph>
              The following activities are strictly prohibited:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>Using AI tools to generate any content</li>
              <li>Attempting to hack or compromise the platform</li>
              <li>Creating multiple accounts</li>
              <li>Impersonating other users</li>
              <li>Distributing malware or harmful code</li>
              <li>Scraping or automated data collection</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              8. Moderation and Enforcement
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>Review and moderate all content</li>
              <li>Remove content that violates these terms</li>
              <li>Suspend or terminate accounts for violations</li>
              <li>Report illegal activities to authorities</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              9. Privacy and Data
            </Typography>
            <Typography variant="body1" paragraph>
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
              use, and protect your information. By using this service, you consent to our data practices 
              as described in the Privacy Policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              10. Disclaimers
            </Typography>
            <Typography variant="body1" paragraph>
              The service is provided "as is" without warranties of any kind. We do not guarantee:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>Uninterrupted or error-free service</li>
              <li>Security of user data (though we implement best practices)</li>
              <li>Accuracy or reliability of user-generated content</li>
              <li>Compatibility with all devices or browsers</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              11. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, or any loss of profits or revenues.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              12. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              Either party may terminate this agreement at any time:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
              <li>You may delete your account at any time</li>
              <li>We may suspend accounts for terms violations</li>
              <li>Upon termination, your access to the service will cease</li>
              <li>Some provisions of these terms may survive termination</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              13. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We may modify these terms at any time. Changes will be effective immediately upon posting. 
              Your continued use of the service constitutes acceptance of the modified terms.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              14. Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These terms shall be governed by and construed in accordance with applicable laws. 
              Any disputes shall be resolved through binding arbitration.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              15. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about these Terms of Service, please contact us at:
            </Typography>
            <Typography variant="body1">
              Email: support@chscw.club
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Terms;
